import { ArrowsAltOutlined, DownOutlined } from '@ant-design/icons';
import { Button, Col, Dropdown, MenuProps, Popover, Row, Space, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { NormType, SignatureFigureType } from '../../../types/analysisTypes';
import FullscreenGraphicModal from '../FullscreenGraphicModal';
import { EncodedSignatureLink } from './EncodedSignatureLink';
import { SingleSignatureFigure } from './SingleSignatureFigure';
import { SignaturePie } from './SignaturePie';
import { useAppSelector } from '../../../app/hooks';
import { selectChemicalCalibrationItemNames, selectHumidityCompensationCalibrantName, selectSessionID } from '../../../features/analysisConfig/analysisConfigSlice';
import { WithFeatureFlagsCheck } from '../../../with_feature_flags_check';
import { useAuth0 } from '@auth0/auth0-react';
import { FeatureFlag, UserClaimsWithTSDB } from '../../../types/userType';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const SingleSignatureWidget: React.FC = () => {
  const sessionID = useAppSelector(selectSessionID);
  const [figureType, setFigureType] = useState<SignatureFigureType>(+SignatureFigureType.Rose);
  const [normType, setNormType] = useState<NormType>(+NormType.Normalized);
  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);

  const { user, isAuthenticated } = useAuth0();
  const [userInfo, setUserInfo] = useState<UserClaimsWithTSDB | null>(null);

  const humidityCalibrationCalibrantName = useAppSelector(selectHumidityCompensationCalibrantName);
  const chemicalCalibrationItemNames = useAppSelector(selectChemicalCalibrationItemNames);

  const renderMenuItemsWithFeatureFlags = () => {
    if (userInfo === null) return;

    let items: NonNullable<MenuProps['items']> = [];
    Object.entries(SignatureFigureType)
      .filter(([_, value]) => typeof value === 'number')
      .filter(([_, value]) => {
        if (value === SignatureFigureType.Chemistry) {
          if (userInfo.feature_flags.includes(FeatureFlag.RoleAryballe) || userInfo.feature_flags.includes(FeatureFlag.AAChemicalFamiliesAccessEnabled)) {
            return true;
          }
          return false;
        }
        if (value === SignatureFigureType.Spatial) {
          if (userInfo.feature_flags.includes(FeatureFlag.RoleAryballe) || userInfo.feature_flags.includes(FeatureFlag.AANonAggregatedPeptidesAccessEnabled)) {
            return true;
          }
          return false;
        }
        return true;
      })
      .forEach(([label, value]) => {
        switch (value) {
          case SignatureFigureType.Chemistry:
            if (userInfo.feature_flags.includes(FeatureFlag.RoleAryballe) || userInfo.feature_flags.includes(FeatureFlag.AAChemicalFamiliesAccessEnabled)) {
              items.push({
                key: +value,
                label: <WithFeatureFlagsCheck ff={FeatureFlag.AAChemicalFamiliesAccessEnabled}>{<>{label}</>}</WithFeatureFlagsCheck>, //label,
                onClick: () => {
                  setFigureType(+value);
                },
              });
            }
            break;
          case SignatureFigureType.Spatial:
            if (userInfo.feature_flags.includes(FeatureFlag.RoleAryballe) || userInfo.feature_flags.includes(FeatureFlag.AANonAggregatedPeptidesAccessEnabled)) {
              items.push({
                key: +value,
                label: <WithFeatureFlagsCheck ff={FeatureFlag.AANonAggregatedPeptidesAccessEnabled}>{<>{label}</>}</WithFeatureFlagsCheck>, //label,
                onClick: () => {
                  setFigureType(+value);
                },
              });
            }
            break;
          default:
            items.push({
              key: +value,
              label: label,
              onClick: () => {
                setFigureType(+value);
              },
            });
        }
      });
    return items;
  };

  useEffect(() => {
    if (!user || !isAuthenticated) setUserInfo(null);
    else setUserInfo(user as UserClaimsWithTSDB);
  }, [isAuthenticated, user]);

  if (userInfo === null) return null;

  return (
    <div style={{ width: '100%', height: '40vh', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
      <Row justify="space-between" align="middle">
        <Col></Col>
        <Col>
          <Space align="center" style={{ display: 'flex', justifyContent: 'center' }}>
            <Dropdown
              menu={{
                items: Object.entries(NormType)
                  .filter(([_, value]) => typeof value === 'number')
                  .map(([label, value]) => {
                    return {
                      key: +value,
                      label: label,
                      onClick: () => {
                        setNormType(+value);
                      },
                    };
                  }),
              }}
            >
              <Tooltip title="Normalization">
                <Button style={{ borderRadius: '5px' }}>
                  <Space>
                    {NormType[normType]}
                    <DownOutlined />
                  </Space>
                </Button>
              </Tooltip>
            </Dropdown>
            <Dropdown menu={{ items: renderMenuItemsWithFeatureFlags() }}>
              <Tooltip title="Chart type">
                <Button style={{ borderRadius: '5px' }}>
                  <Space>
                    {SignatureFigureType[figureType]}
                    <DownOutlined />
                  </Space>
                </Button>
              </Tooltip>
            </Dropdown>
            <EncodedSignatureLink />
          </Space>
        </Col>
        <Col style={{ display: 'flex', alignItems: 'center' }}>
          {chemicalCalibrationItemNames && chemicalCalibrationItemNames.length > 0 && normType === NormType.Normalized && (
            <Popover style={{ display: 'flex' }} trigger={'hover'} content="Chemical calibration is active">
              <FontAwesomeIcon icon="balance-scale" style={{ marginRight: 10, fontSize: '13pt' }} />
            </Popover>
          )}
          {humidityCalibrationCalibrantName && (
            <Popover style={{ display: 'flex' }} trigger={'hover'} content="Humidity correction is active">
              <FontAwesomeIcon icon="droplet" style={{ marginRight: 10, fontSize: '13pt' }} />
            </Popover>
          )}
          <ArrowsAltOutlined className="clickable-icon" onClick={() => setIsVisibleModal(true)} />
        </Col>
      </Row>
      <>
        {(function () {
          if (figureType === +SignatureFigureType.Chemistry) return <SignaturePie sessionID={sessionID} />;
          else return <SingleSignatureFigure figureType={figureType} normType={normType} />;
        })()}
      </>
      <FullscreenGraphicModal title="Signature" visible={isVisibleModal} onCancel={() => setIsVisibleModal(false)}>
        {(function () {
          if (figureType === +SignatureFigureType.Chemistry) return <SignaturePie sessionID={sessionID} />;
          else return <SingleSignatureFigure figureType={figureType} normType={normType} />;
        })()}
      </FullscreenGraphicModal>
    </div>
  );
};
